import "./ProjectAccessEditFields.css";

import { FormEvent } from "react";

import { ErrorBox } from "../../cards";
import { ReadonlyText } from "../../data";
import { ActionButtons, Button, Checkbox, FormLabel, KanaTooltip, Select } from "../../forms";
import { Card, Column, Row } from "../../layout";
import { getErrorMessages, hasFieldError } from "../utils";
import { ProjectAccessEditFieldsProps } from "./models";

// PLACEHOLDER - replace with the Figma copy.
const ORGANISATION_TYPE_TOOLTIP_TEXT =
  "The type of organisation invited to the project. Full access grants the permissions of this organisation type.";

// PLACEHOLDER - replace with the Figma copy.
const MAKE_RESPONSIBLE_OWNER_TOOLTIP_TEXT =
  "The responsible owner manages access to this project. Assigning it here removes the role from the organisation that currently holds it.";

export const ProjectAccessEditFields = ({
  organisationName,
  organisationType,
  email,
  invitedBy,
  phoneNumber,
  dateSent,
  accessOptions,
  value,
  onChange,
  onSubmit,
  onCancel,
  showMakeResponsibleOwner,
  canRevoke,
  onRevoke,
  canResend,
  onResend,
  isReadOnly = false,
  isLoading = false,
  corners = "default",
  errors,
}: ProjectAccessEditFieldsProps): JSX.Element => {
  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <Card corners={corners}>
      <form className="ProjectAccessEditFields" onSubmit={handleSubmit}>
        <Row spacingV="ll">
          <Column span={12}>
            <div className="ProjectAccessEditFields_header">
              <h2>Project access</h2>
              <div className="ProjectAccessEditFields_headerActions">
                {canRevoke && (
                  <Button
                    variant={canResend ? "secondary" : "tertiary"}
                    text="Revoke access"
                    corners={corners}
                    onClick={onRevoke}
                  />
                )}
                {canResend && (
                  <Button variant="tertiary" text="Resend invitation" corners={corners} onClick={onResend} />
                )}
              </div>
            </div>
          </Column>
        </Row>

        {errors && errors.length > 0 && (
          <Row spacingV="ml">
            <Column span={12}>
              <ErrorBox title="Please solve the below issues before saving" text={getErrorMessages(errors)} />
            </Column>
          </Row>
        )}

        <Row spacingV="ll">
          <Column span={6}>
            <ReadonlyText
              label="Organisation type"
              labelJustify="SpaceBetween"
              value={organisationType}
              tooltipHeader=""
              tooltipText={ORGANISATION_TYPE_TOOLTIP_TEXT}
            />
          </Column>
          <Column span={6}>
            <ReadonlyText label="Organisation" value={organisationName} />
          </Column>
        </Row>

        <Row spacingV="ll">
          <Column span={6}>
            <ReadonlyText label="Email" value={email} />
          </Column>
          <Column span={6}>
            <ReadonlyText label="Invited by" value={invitedBy} />
          </Column>
        </Row>

        <Row spacingV="ll">
          <Column span={6}>
            <ReadonlyText label="Phone number" value={phoneNumber} />
          </Column>
          <Column span={6}>
            <ReadonlyText label="Date sent" value={dateSent} />
          </Column>
        </Row>

        <Row spacingV="4xl">
          <Column span={6}>
            {isReadOnly ? (
              <ReadonlyText label="Access" value={value.access} />
            ) : (
              <Select
                label="Access"
                placeholder="Select"
                corners={corners}
                data={accessOptions}
                value={value.access}
                onChange={(access) => onChange({ ...value, access, makeResponsibleOwner: false })}
                hasError={hasFieldError(errors, "access")}
              />
            )}
          </Column>
          {showMakeResponsibleOwner && (
            <Column span={6}>
              <div className="ProjectAccessEditFields_checkbox">
                <FormLabel
                  label="Make Responsible Owner"
                  tooltip={<KanaTooltip tooltipHeader="" tooltipText={MAKE_RESPONSIBLE_OWNER_TOOLTIP_TEXT} />}
                />
                <Checkbox
                  label="Confirm"
                  checked={value.makeResponsibleOwner ?? false}
                  disabled={isReadOnly}
                  onChange={(e) => onChange({ ...value, makeResponsibleOwner: e.target.checked })}
                />
              </div>
            </Column>
          )}
        </Row>

        <Row>
          <Column span={6}>
            <ActionButtons
              submitButtonLabel="Save"
              cancelButtonLabel="Cancel"
              corners={corners}
              onCancel={onCancel}
              isLoading={isLoading}
              disabled={isReadOnly}
            />
          </Column>
        </Row>
      </form>
    </Card>
  );
};
